import { useTranslation } from 'react-i18next';
import classnames from '../../../core/utils/classnames.tsx';
import * as React from 'react';
import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { AssistanceField } from '../../../document/pages/Assistance/AssistanceField.tsx';
import AssistanceSection from '../../../document/pages/Assistance/AssistanceSection.tsx';
import Button from '../../../core/components/Button.tsx';
import { faCheckCircle, faGripDotsVertical, faPlus, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { withIcon } from '../../../core/components/Icon.tsx';
import {
    FormField,
    FormFieldConfig,
    getFormFieldGroups,
    useDocumentConfig,
} from '../../../document/pages/Assistance/useDocumentConfig.ts';
import { useDrag, useDrop } from 'react-dnd';
import { useAssistanceContext } from '../../../document/pages/Assistance/AssistanceContext.tsx';
import Suggestions, { ISuggestion } from './Suggestions.tsx';
import CompleteButton from './CompleteButton.tsx';

const POSITION_FIELDS_CONFIG_KEY = 'positions__article_sets__articles';

interface IFieldData {
    value?: string;
    predictionConfidence?: number;
    confidenceExplanation?: {
        translationKey: string;
        explanationDetails?: {
            confidence: number;
            field_value: string;
        };
    };
    choices?: string[]; // list of options comes from extracted data
    // ... and more but not sure if it's needed here
    [key: string]: any;
}

export interface IArticle {
    number: IFieldData;
    description: IFieldData;
    quantity: IFieldData;
    unit: IFieldData;
    comments: IFieldData;
    suggestions?: ISuggestion[];
}

export const DRAGGABLE_ITEM_TYPE = 'POSITION_ARTICLE_GROUP_CARD';

const AddIcon = withIcon(faPlus);
const RemoveIcon = withIcon(faTrash);
const CompleteIcon = withIcon(faCheckCircle);
const DragIcon = withIcon(faGripDotsVertical);

const ArticleAssistanceField = ({
    fieldName,
    valueKey = 'value',
    value,
    placeholder = '',
    // event handler
    onUpdate = undefined,
    data = {},
    // config stuff
    config,
    generalConfig,
}: {
    fieldName: string;
    valueKey?: string;
    value: string;
    placeholder?: string;
    // event handler
    onUpdate?: (value: string) => Promise<any> | void;
    data?: IFieldData;
    // config stuff
    config?: FormFieldConfig;
    generalConfig?: any;
}) => {
    const { readOnly, loading: documentLoading } = useAssistanceContext();

    const [isUpdateActive, setIsUpdateActive] = useState(false);
    const [isFocusActive, setIsFocusActive] = useState(false);

    const handleUpdate = ({ payload }) => {
        setIsUpdateActive(true);
        return Promise.resolve(onUpdate?.(payload)).finally(() => {
            setIsUpdateActive(false);
        });
    };

    const handleFocus = () => {
        setIsFocusActive(true);
    };

    const handleBlur = () => {
        setIsFocusActive(false);
    };

    return (
        <AssistanceField
            fieldName={fieldName}
            valueKey={valueKey}
            value={value}
            placeholder={placeholder}
            data={data}
            config={config}
            generalConfig={generalConfig}
            onUpdate={handleUpdate}
            onFocus={handleFocus}
            onBlur={handleBlur}
            isUpdateActive={isUpdateActive}
            isFocusActive={isFocusActive}
            documentReadOnly={readOnly}
            documentLoading={documentLoading}
        />
    );
};

const ArticleFormContext = createContext<{ fields: FormField[]; article?: any }>({ fields: [] });

export const ArticleFormContextProvider = ({
    children,
    ...props
}: {
    fields: FormField[];
    article?: any;
    children: any;
}) => <ArticleFormContext.Provider value={props}>{children}</ArticleFormContext.Provider>;
export const useArticleFormContext = () => useContext(ArticleFormContext);

const ArticleForm = ({
    className,
    articleIndex,
    article,
    fieldGroups,
    onArticleChange,
    onRemove,
    generalConfig,
}: {
    className?: string;
    articleIndex: number;
    article?: any;
    fieldGroups: FormField[][];
    onArticleChange: (fieldName: string, payload: any) => void;
    onRemove?: () => void;
    generalConfig: any;
}) => {
    const { readOnly, loading } = useAssistanceContext();

    const { t } = useTranslation('assistance');

    return (
        <ArticleFormContextProvider fields={fieldGroups.flat()} article={article}>
            <AssistanceSection.FieldGroup
                title={t('listOfServices.articleSetCard.formTitle', { number: articleIndex + 1 })}
                controls={
                    onRemove && (
                        <AssistanceSection.FieldGroupIconButton
                            disabled={readOnly || loading}
                            onClick={onRemove}
                            className="hover:text-content-danger-strong hover:bg-surface-danger-weak"
                        >
                            <RemoveIcon />
                        </AssistanceSection.FieldGroupIconButton>
                    )
                }
                className={className}
            >
                {fieldGroups.map((fieldGroup: FormField[]) => {
                    const formField = fieldGroup[0];
                    return (
                        <AssistanceSection.FieldLabel
                            key={formField.fieldName}
                            label={t(`listOfServices.articleSetCard.fieldNames.${formField.fieldName}`)}
                            required={formField.config.isRequired}
                        >
                            <ArticleAssistanceField
                                fieldName={formField.fieldName}
                                value={formField.value}
                                onUpdate={(payload) => {
                                    return onArticleChange(formField.fieldName, payload);
                                }}
                                placeholder={t(`listOfServices.articleSetCard.fieldNames.${formField.fieldName}`)}
                                data={formField.data}
                                config={formField.config}
                                generalConfig={generalConfig}
                            />
                        </AssistanceSection.FieldLabel>
                    );
                })}
            </AssistanceSection.FieldGroup>
        </ArticleFormContextProvider>
    );
};

const MemoArticleForm = React.memo(ArticleForm);

export const Separator = ({
    className,
    borderClassName,
    children,
}: {
    className?: string;
    borderClassName?: string;
    children?: React.ReactNode;
}) => {
    return (
        <div className={classnames('relative flex items-center w-full', className)}>
            <div className={classnames('border-b border-solid border-line-weak flex-1', borderClassName)} />

            {children && <div className="text-content-medium text-sm px-4 -mt-0.5">{children}</div>}

            <div className={classnames('border-b border-solid border-line-weak flex-1', borderClassName)} />
        </div>
    );
};

const useDragAndDrop = ({
    index,
    onDragStart,
    onMove,
    onDrop,
    acceptedDropKey,
}: {
    index: number;
    onDragStart?: any;
    onMove?: any;
    onDrop?: any;
    acceptedDropKey?: string;
}) => {
    const dragRef = useRef(null);
    const previewRef = useRef(null);
    const dropRef = useRef(null);

    const [, drop] = useDrop({
        accept: acceptedDropKey || DRAGGABLE_ITEM_TYPE,
        hover(item: any, monitor) {
            if (!dragRef.current) {
                return;
            }

            const dragIndex = item.index;
            const hoverIndex = index;

            // Determine rectangle on screen
            const hoverBoundingRect = dropRef.current?.getBoundingClientRect();

            // Get vertical middle
            const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

            // Determine mouse position
            const clientOffset = monitor.getClientOffset();

            // Get pixels to the top
            const hoverClientY = clientOffset.y - hoverBoundingRect.top;

            // Don't replace items with themselves
            if (dragIndex === hoverIndex) {
                onMove?.(null);
                return;
            } else if (hoverClientY < hoverMiddleY) {
                // upper half
                onMove?.(hoverIndex);
                return;
            } else {
                // lower half
                onMove?.(hoverIndex + 1);
                return;
            }
        },
    });

    const [{ isDragging }, drag, dragPreview] = useDrag({
        type: acceptedDropKey || DRAGGABLE_ITEM_TYPE,
        item: () => {
            const item = { index };
            onDragStart?.(item);
            return item;
        },
        collect: (monitor: any) => ({
            isDragging: monitor.isDragging(),
        }),
        end: (item, monitor) => {
            onDrop?.(item);
        },
    });

    drag(dragRef);
    drop(dragPreview(previewRef));
    drop(dropRef);

    return { dragRef, previewRef, dropRef, isDragging, drag, drop };
};

export const DropPreview = ({
    className,
    index,
    acceptedDropKey,
}: {
    className?: string;
    index: number;
    acceptedDropKey?: string;
}) => {
    const { dropRef } = useDragAndDrop({ index, acceptedDropKey });
    return <div className={classnames('bg-surface-strong rounded-lg h-8 w-full', className)} ref={dropRef} />;
};

// this is not being used
const PositionArticleSetCard = ({
    className,
    articles,
    onArticlesChange,
    isComplete,
    onIsCompleteChange,
    documentConfig,
    setIndex = 0,
    showArticles,
    sortIndex,
    onSortIndexChange,

    onDragStart,
    onMove,
    onDrop,
    acceptedDropKey,
}: {
    className?: string;
    articles: IArticle[];
    onArticlesChange: (action: string, payload?: any) => void;
    isComplete: boolean;
    onIsCompleteChange: (isComplete: boolean) => void;
    documentConfig: any;
    setIndex: number;
    showArticles?: boolean;
    sortIndex?: string;
    onSortIndexChange?: (sortIndex: string) => void;

    onDragStart?: any;
    onMove?: any;
    onDrop?: any;
    acceptedDropKey?: string;
}) => {
    const { readOnly, loading } = useAssistanceContext();

    const { t } = useTranslation('assistance');

    const [isOpen, setIsOpen] = useState(true);
    const [localSortIndex, setLocalSortIndex] = useState(sortIndex || '');

    useEffect(() => {
        setLocalSortIndex(sortIndex || '');
    }, [sortIndex]);

    useEffect(() => {
        setIsOpen(showArticles);
    }, [showArticles]);

    const { generalConfig, fieldConfigsByPrefix } = useDocumentConfig(documentConfig, [POSITION_FIELDS_CONFIG_KEY]);
    const articleFieldConfigs = fieldConfigsByPrefix[POSITION_FIELDS_CONFIG_KEY];

    const handleIsCompleteChange = (isComplete: boolean) => {
        setIsOpen(isArticleSetEmpty);
        return onIsCompleteChange(isComplete);
    };

    // drag and drop
    const { dragRef, dropRef, isDragging } = useDragAndDrop({
        index: setIndex,
        onDragStart,
        onMove,
        onDrop,
        acceptedDropKey,
    });

    const articleNumbers = articles.map((article) => article.number.value);

    const getIsArticleEmpty = (article: IArticle) => {
        const fieldGroups = getFormFieldGroups(article, articleFieldConfigs, POSITION_FIELDS_CONFIG_KEY);
        return fieldGroups.every((fields) => fields.every((field) => !field?.value));
    };

    const shouldShowSuggestions = (article: IArticle) => {
        const fieldGroups = getFormFieldGroups(article, articleFieldConfigs, POSITION_FIELDS_CONFIG_KEY);

        return fieldGroups.every((fields) =>
            fields
                .filter((field) => !(field.fieldName === 'quantity' || field.fieldName === 'unit'))
                .every((field) => !field?.value)
        );
    };

    const articlesFieldGroups = articles.map((article): [IArticle, FormField[][]] => {
        const isArticleEmpty = getIsArticleEmpty(article);
        const fieldGroups = getFormFieldGroups(article, articleFieldConfigs, POSITION_FIELDS_CONFIG_KEY);

        // when the article is empty fields should not be required
        if (isArticleEmpty) {
            fieldGroups.forEach((fields) => {
                fields.forEach((field) => {
                    field.config.isRequired = false;
                });
            });
        }

        return [article, fieldGroups];
    });

    const lastArticle = articles[articles.length - 1];
    const isLastArticleEmpty = getIsArticleEmpty(lastArticle);
    const isArticleSetEmpty = articles.every((article) => getIsArticleEmpty(article));

    const showCompleteButton = !isArticleSetEmpty;
    const showAddArticleButton = !isLastArticleEmpty;
    const showSuggestions = shouldShowSuggestions(lastArticle);

    const handleSelectSuggestion = (articleIndex: number, suggestion: ISuggestion) => {
        return onArticlesChange('action:change_article_field', { articleIndex, setIndex, _lookupEntity: suggestion });
    };

    const canCompleteArticles = articlesFieldGroups.every(([, fieldGroups]) =>
        fieldGroups.every((fields) =>
            fields.every((field) => {
                return !field.config.isRequired || Boolean(field.value);
            })
        )
    );

    return (
        <div ref={dropRef} className={classnames(isDragging && 'bg-surface-strong rounded-lg', className)}>
            <div
                className={classnames(
                    'flex flex-col rounded-lg shadow-md bg-surface-weak border border-line-weak border-solid overflow-hidden',
                    isDragging && '!outline outline-line-brand-weak outline-2'
                )}
            >
                {setIndex === 0 && onSortIndexChange && (
                    <div className="px-6 py-4 border-b border-line-weak">
                        <div className="flex gap-1">
                            <label className="text-xs text-content-medium w-36 flex-none py-2">
                                {t('listOfServices.positionCard.sortIndex')}
                            </label>
                            <input
                                type="text"
                                value={localSortIndex}
                                onChange={(e) => {
                                    setLocalSortIndex(e.target.value);
                                }}
                                onBlur={() => {
                                    if (localSortIndex !== (sortIndex || '')) {
                                        onSortIndexChange(localSortIndex);
                                    }
                                }}
                                disabled={readOnly || loading}
                                className="flex-1 border border-line-weak rounded px-3 py-2 text-sm disabled:bg-surface-weak disabled:text-content-disabled focus:outline-none focus:border-line-brand-weak"
                            />
                        </div>
                    </div>
                )}
                <AssistanceSection
                    title={t('listOfServices.articleSetCard.title', { articles: articleNumbers.join(', ') })}
                    controls={
                        <>
                            {showCompleteButton && (
                                <CompleteButton
                                    isComplete={isComplete}
                                    disabled={!canCompleteArticles}
                                    onIsCompleteChange={handleIsCompleteChange}
                                />
                            )}
                            <div
                                ref={dragRef}
                                className="flex items-center justify-center rounded transition-colors duration-200 h-8 hover:bg-surface-strong cursor-move px-2"
                            >
                                {!readOnly && <DragIcon />}
                            </div>
                        </>
                    }
                    open={isOpen}
                    onOpenChange={setIsOpen}
                >
                    <div className="flex flex-col gap-8">
                        {articlesFieldGroups?.map(([article, fieldGroups], index) => {
                            return (
                                <React.Fragment key={`article-${index}`}>
                                    <MemoArticleForm
                                        // key has to be unique so if one group is deleted it doesn't
                                        // look like the one below triggered an update on it
                                        // Note: there should be an uuid field in the article object
                                        key={`article-${index}`}
                                        articleIndex={index}
                                        article={article}
                                        fieldGroups={fieldGroups}
                                        onArticleChange={(fieldName, payload) => {
                                            return onArticlesChange('action:change_article_field', {
                                                articleIndex: index,
                                                fieldName: fieldName,
                                                ...payload, // Can be only a value or also a lookupEntity
                                            });
                                        }}
                                        onRemove={
                                            setIndex !== 0 || (articles && articles.length > 1)
                                                ? () =>
                                                      onArticlesChange('action:remove_article', {
                                                          articleIndex: index,
                                                      })
                                                : undefined
                                        }
                                        generalConfig={generalConfig}
                                    />

                                    {index < articles.length - 1 && (
                                        <Separator>{t('listOfServices.articleSetCard.and')}</Separator>
                                    )}
                                </React.Fragment>
                            );
                        })}

                        {showAddArticleButton && (
                            <div className="flex justify-center">
                                <Button
                                    disabled={readOnly || loading}
                                    variant="ghost"
                                    onClick={() => onArticlesChange('action:add_article')}
                                >
                                    <AddIcon /> {t('listOfServices.articleSetCard.addArticle')}
                                </Button>
                            </div>
                        )}

                        {showSuggestions && (
                            <Suggestions
                                suggestions={articles[0]?.suggestions || []}
                                onSelect={(suggestion) => handleSelectSuggestion(articles.length - 1, suggestion)}
                            />
                        )}
                    </div>
                </AssistanceSection>
            </div>
        </div>
    );
};

export default PositionArticleSetCard;
